/**
 * ReDoS safety screen for Chat Security (`cursor-database`) scan patterns.
 *
 * Scan patterns are operator-configurable (`maliciousPatterns` /
 * `credentialPatterns` / `commandPatterns`), so they are untrusted input: a
 * pattern with catastrophic backtracking would hang the scan worker on
 * attacker-chosen content. Every pattern is screened here before it is ever
 * passed to `new RegExp`.
 *
 * It lives in api-client so that one definition decides at all three points a
 * pattern is judged: the backend scanners at scan time, the backend settings
 * write at save time (for rules being added), and the dashboard's "Add Rule"
 * form and "Never matches" marker. If they disagreed, a rule could be accepted
 * on save and then silently skipped by the scanner.
 *
 * The module has no imports and no Node built-ins, so it runs unchanged in the
 * browser. Reasons do not repeat the pattern: callers that log a rejection
 * attach the pattern as its own field, and the UI shows it next to the input.
 */
/** Maximum regex pattern length for safety */
export const MAX_PATTERN_LENGTH = 1000;
/**
 * An unbounded `{n,}` quantifier at `lastIndex`. Digits only: JS reads `{,}`
 * or `{x,}` as literal text, not as a quantifier. Sticky, so a test reads
 * just the characters after one `{` and the whole check stays linear.
 */
const OPEN_ENDED_BRACE_QUANTIFIER = /\{\d+,\}/y;
/**
 * Whether any positive lookahead `(?=…)` in the pattern has an unbounded
 * quantifier — `*`, `+` or `{n,}` — inside its own body.
 *
 * This replaces twelve regex heuristics of the form "`(?=`, then `.*`, then a
 * quantifier", whose `.*` ran straight past the lookahead's closing paren, so
 * any quantifier later in the pattern counted. That rejected the shipped
 * `xxd … -r … | sh` rule, whose lookahead `(?=\s|\||$)` has no quantifier at
 * all — the `*` it tripped on was in a later `\s*`.
 *
 * One pass with a stack of open groups: a quantifier counts when at least one
 * enclosing group is a lookahead, so nested groups and nested lookaheads are
 * part of the body. Escaped characters and character classes are skipped, so
 * `\*` and `[*+]` are literals. An unclosed lookahead stays open to the end of
 * the pattern, which is conservative; such a pattern does not compile anyway.
 * The pass is linear in the pattern length: this runs on every settings save,
 * on input an API caller controls.
 */
function hasQuantifiedLookahead(pattern) {
    const openGroupIsLookahead = [];
    let openLookaheads = 0;
    let inClass = false;
    for (let i = 0; i < pattern.length; i++) {
        const ch = pattern[i];
        if (ch === '\\') {
            i++;
            continue;
        }
        if (inClass) {
            // JS closes a class at its first unescaped `]`, even right after `[`.
            if (ch === ']') {
                inClass = false;
            }
            continue;
        }
        switch (ch) {
            case '[':
                inClass = true;
                break;
            case '(': {
                const isLookahead = pattern.startsWith('(?=', i);
                openGroupIsLookahead.push(isLookahead);
                if (isLookahead) {
                    openLookaheads++;
                }
                break;
            }
            case ')':
                if (openGroupIsLookahead.pop()) {
                    openLookaheads--;
                }
                break;
            case '*':
            case '+':
                if (openLookaheads > 0) {
                    return true;
                }
                break;
            case '{':
                if (openLookaheads > 0) {
                    OPEN_ENDED_BRACE_QUANTIFIER.lastIndex = i;
                    if (OPEN_ENDED_BRACE_QUANTIFIER.test(pattern)) {
                        return true;
                    }
                }
                break;
        }
    }
    return false;
}
/**
 * Validate regex pattern for ReDoS safety before compilation
 */
export function isSafeRegexPattern(pattern) {
    // Check for excessive complexity first: every check below reads the whole
    // pattern, and this one is O(1), so an oversized pattern costs nothing more.
    if (pattern.length > MAX_PATTERN_LENGTH) {
        return {
            safe: false,
            reason: `Pattern too complex (${pattern.length} chars, max ${MAX_PATTERN_LENGTH})`,
        };
    }
    // Check for catastrophic backtracking inside a positive lookahead
    if (hasQuantifiedLookahead(pattern)) {
        return {
            safe: false,
            reason: 'Pattern contains potentially dangerous backtracking',
        };
    }
    // Check for nested quantifiers (basic check)
    const nestedQuantifiers = /(\*|\+|\{[^}]*,\})\s*(\*|\+|\{[^}]*,\})/;
    if (nestedQuantifiers.test(pattern)) {
        return {
            safe: false,
            reason: 'Pattern contains nested quantifiers',
        };
    }
    // Check for greedy quantifiers followed by other characters (e.g., .*abc, .+xyz, .*>&)
    // This can cause exponential backtracking in cases like "bash.*>&"
    // Match: .*, .+, or .{n,} followed by any non-anchor character
    const greedyDotFollowedByLiteral = /\.\s*(\*|\+|\{[^}]*,\})\s*[^$\\)|\]]/;
    if (greedyDotFollowedByLiteral.test(pattern)) {
        return {
            safe: false,
            reason: 'Pattern contains greedy dot quantifier followed by literals (potential exponential backtracking)',
        };
    }
    // Check for patterns like (.*something)+ or (.+something)* which can cause nested backtracking
    const nestedGreedyGroups = /\([^)]*(\.\*|\.\+)[^)]*\)\s*(\*|\+|\{[^}]*,\})/;
    if (nestedGreedyGroups.test(pattern)) {
        return {
            safe: false,
            reason: 'Pattern contains nested greedy groups (potential exponential backtracking)',
        };
    }
    return { safe: true };
}
/**
 * Why a Chat Security pattern can never match, or `undefined` if it is usable.
 *
 * A pattern is unusable when the safety screen rejects it (the scanner skips
 * it) or when it does not compile (the scanner drops it). Both checks are
 * needed: the screen passes some strings that are not valid regexes.
 */
export function getRegexPatternRejection(pattern) {
    const screen = isSafeRegexPattern(pattern);
    if (!screen.safe) {
        return screen.reason;
    }
    try {
        new RegExp(pattern, 'gi');
    }
    catch {
        return 'Invalid regular expression';
    }
    return undefined;
}
//# sourceMappingURL=regex-safety.js.map